import clsx from "clsx";
import React, { useCallback, useEffect, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";
import { useHistory, useRouteMatch } from "react-router-dom";

import Back from "~/components/Icons/arrow-prev2";
import IconCopy from "~/components/Icons/icon-copy";
import Preloader from "~/components/Preloader";
import Breadcrumbs from "~/components/UI/Breadcrumbs";
import Switch from "~/components/UI/Switch";
import useCopyRole from "~/pages/RegistryOfRole/hooks/useCopyRole";
import useGetDescriptionsRole from "~/pages/RegistryOfRole/hooks/useGetDescriptionsRole";
import useGetRole from "~/pages/RegistryOfRole/hooks/useGetRole";
import useUpdateRole from "~/pages/RegistryOfRole/hooks/useUpdateRole";
import AccordionPermissions from "~/pages/RegistryOfRole/RoleModel/AccordionPermissions";
import Button from "~/shared/ui/Fields/Button";
import Input from "~/shared/ui/Fields/Input";
import SelectUI from "~/shared/ui/Fields/Select";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { CopyRoleModal } from "~/pages/RegistryOfRole/CopyRoleModal";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useNotify } from "~/shared/hooks/useNotify";

import { Regional } from "./Regional/Regional";

import s from "./RoleModel.less";

interface FormData {
  role: string;
  dealerChoice: {
    value: string;
    label: string;
  };
  isActive: boolean;

  isRegionalManager: boolean;
  isLeaderRegionalManager: boolean;
  regionalManagerDirection: number[];
  leaderRegionalManagerDirection: number[];
}

const RoleModel = () => {
  const { t } = useTranslation();
  const { notifyError } = useNotify();
  const {
    hasPermission,
    loading: loadingPermissions,
    refetch: refetchPermissions,
  } = usePermissions();
  const { params } = useRouteMatch<{ id: string; period?: string }>();
  const IdParams = params["id"];
  const history = useHistory();
  const isAdd = IdParams === "add";
  const { openChildModal, status } = useGetLocationData();
  const setData = useSetLocationData();
  const { data, refetch, isLoading } = useGetDescriptionsRole();
  const {
    data: dataRole,
    refetch: refetchRole,
    isLoading: loadingRole,
  } = useGetRole(IdParams);
  const updateRoleMutation = useUpdateRole();
  const { copyRoleMutation, copyRoleId, setCopyRoleId, doCopy } = useCopyRole(
    "Ошибка при копирования роли::",
  );

  const {
    control,
    watch,
    setValue,
    getValues,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      role: "",
      dealerChoice: { label: t(`roles.all`), value: "all" },
      isActive: true,
      isRegionalManager: false,
      isLeaderRegionalManager: false,
      regionalManagerDirection: [],
      leaderRegionalManagerDirection: [],
    },
    resolver: yupResolver(
      yup.object().shape({
        regionalManagerDirection: yup.array().when("isRegionalManager", {
          is: true,
          then: (schema) => schema.min(1, t("validation.required")),
          otherwise: (schema) => schema.notRequired(),
        }),
        leaderRegionalManagerDirection: yup
          .array()
          .when("isLeaderRegionalManager", {
            is: true,
            then: (schema) => schema.min(1, t("validation.required")),
            otherwise: (schema) => schema.notRequired(),
          }),
      }),
    ),
  });

  const isRegionalManager = watch("isRegionalManager");
  const isLeaderRegionalManager = watch("isLeaderRegionalManager");

  const onRegionalCheckboxChange = (checked: boolean) => {
    if (!checked) {
      setValue("regionalManagerDirection", []);
    }
  };

  const onLeaderCheckboxChange = (checked: boolean) => {
    if (!checked) {
      setValue("leaderRegionalManagerDirection", []);
    } else {
      const currentRegionalDirection = getValues("regionalManagerDirection");
      const currentIsRegionalManager = getValues("isRegionalManager");
      if (
        currentIsRegionalManager &&
        Array.isArray(currentRegionalDirection) &&
        currentRegionalDirection.length > 0
      ) {
        setValue("leaderRegionalManagerDirection", currentRegionalDirection);
      }
    }
  };

  const [checkedPermissions, setCheckedPermissions] = useState<
    Record<string, boolean>
  >({});

  const resetMethod = () => {
    const backendDealerChoice = dataRole?.role?.dealerChoice || "all";
    reset({
      role: dataRole?.role?.name || "",
      dealerChoice: {
        label: t(`roles.${backendDealerChoice}`),
        value: backendDealerChoice,
      },
      isActive: dataRole?.role?.isActive ?? true,
      isRegionalManager: dataRole?.role?.isRegional ?? false,
      isLeaderRegionalManager: dataRole?.role?.isLeaderRegional ?? false,
      regionalManagerDirection: (
        dataRole?.role?.regionalPositionDirections ?? []
      ).map((d: { instanceId: number }) => d.instanceId),
      leaderRegionalManagerDirection: Array.from(
        new Set(
          [
            ...(dataRole?.role?.regionalPositionDirections ?? []),
            ...(dataRole?.role?.leaderPositionDirections ?? []),
          ].map((d: { instanceId: number }) => d.instanceId),
        ),
      ),
    });
  };

  useEffect(() => {
    if (isAdd) {
      return;
    }
    if (dataRole) {
      resetMethod();
    } else {
      console.log("dataRole нету");
    }
  }, [dataRole, reset, isAdd]);

  const redirectToUserProfilePersonal = useCallback(() => {
    history.push(`/registry_of_role`);
  }, [history]);
  const onSubmit = (formData: FormData) => {
    const roleData = {
      id: IdParams,
      name: formData.role,
      dealerChoice: formData.dealerChoice.value,
      isActive: formData.isActive,
      isRegional: formData.isRegionalManager,
      isLeaderRegional: formData.isLeaderRegionalManager,
      regionalPositionDirectionIds: formData.regionalManagerDirection ?? [],
      leaderPositionDirectionIds: (
        formData.leaderRegionalManagerDirection ?? []
      ).filter((id) => !(formData.regionalManagerDirection ?? []).includes(id)),
      permissions: Object.entries(checkedPermissions).map(
        ([accessDescriptionId, value]) => ({
          id: accessDescriptionId,
          value,
        }),
      ),
    };

    updateRoleMutation.mutate(
      { id: IdParams, roleData },
      {
        onSuccess: () => {
          refetchPermissions();
          history.push(`/registry_of_role`);
        },
        onError: (error) => {
          notifyError(error?.[`message`]);

          resetMethod();
          console.error("Ошибка при сохранении роли:", error);
        },
      },
    );
  };

  const closeModalUser = () => {
    (refetch(), refetchRole(), setData("openChildModal", ""));
  };
  const breadcrumbItems = [
    { link: "/", name: "Главная" },
    { link: "/registry_of_role", name: "Регистр ролей моделей" },
    {
      link: "",
      name: isAdd ? "Создание ролей модели" : "Редактирование ролевой модели",
    },
  ];

  const regionalSettings = [
    {
      nameCheckbox: "isRegionalManager",
      nameSelect: "regionalManagerDirection",
      label: t("roles.regionalManager"),
      isShowSelect: isRegionalManager,
      onCheckboxChange: onRegionalCheckboxChange,
    },
    {
      nameCheckbox: "isLeaderRegionalManager",
      nameSelect: "leaderRegionalManagerDirection",
      label: t("roles.leaderRegionalManager"),
      isShowSelect: isLeaderRegionalManager,
      onCheckboxChange: onLeaderCheckboxChange,
    },
  ];

  if (isLoading || loadingRole) {
    return <Preloader />;
  }

  return (
    <>
      <div className={s.wrapperBlock}>
        <Breadcrumbs
          items={breadcrumbItems}
          onClickHandler={redirectToUserProfilePersonal}
        />
        <div className={s.btn} onClick={redirectToUserProfilePersonal}>
          <Back /> Назад
        </div>
        <div className={s.s_user_title}>
          <div>
            {isAdd ? (
              "Создать роль"
            ) : !!status ? (
              <div>
                Редактировать <br /> скопированную роль
              </div>
            ) : (
              "Редактировать роль"
            )}
          </div>
          {!isAdd && !status && hasPermission("users.access.roles.copy") && (
            <Button
              txtBtn={t(`roles.copy`)}
              onClick={() => setCopyRoleId(IdParams)}
              customClassName={s.btnRole}
            />
          )}
        </div>

        <div className={clsx({ [s.wrapper]: true })}>
          <div className={s.item}>
            <div className={s.title}>Роль</div>
            <Input
              name={"role"}
              customClassName={s.input}
              control={control}
              placeholder="Введите название роли"
            />
          </div>
          <div className={s.item}>
            <AccordionPermissions
              roleActive={dataRole?.role || null}
              title={"Модули и функции"}
              modules={data?.modules || []}
              onPermissionsChange={setCheckedPermissions}
            />
          </div>
          {regionalSettings.map((el) => (
            <div key={el.nameCheckbox} className={s.item}>
              <Regional
                control={control}
                {...el}
                error={errors[el.nameSelect as keyof FormData]}
              />
            </div>
          ))}
          <div className={s.item}>
            <div className={s.title}>Дилеры</div>
            <SelectUI
              control={control}
              name={"dealerChoice"}
              customClassName={s.select}
              newStyle
              optionObjects={[
                { label: t(`roles.all`), value: "all" },
                { label: t(`roles.one`), value: "one" },
                { label: t(`roles.multiple`), value: "multiple" },
                { label: t(`roles.none`), value: "none" },
              ]}
            />
          </div>
          <div className={s.item}>
            <div className={s.title}>Активность</div>
            <Switch name={`isActive`} control={control} disabled={false} />
          </div>

          <div className={s.btnBottom}>
            <Button
              txtBtn={"ОТМЕНА"}
              customClassName={s.btnWrap}
              onClick={redirectToUserProfilePersonal}
            />
            {hasPermission("users.access.roles.edit") && (
              <Button
                txtBtn={"Сохранить"}
                isForm
                orange
                customClassName={s.btnWrap}
                onClick={handleSubmit(onSubmit)}
                disabled={updateRoleMutation.isLoading}
              />
            )}
          </div>
        </div>
      </div>
      <CopyRoleModal
        copyRoleId={copyRoleId}
        setCopyRoleId={setCopyRoleId}
        copyRoleMutation={copyRoleMutation}
        doCopy={doCopy}
        openChildModal={openChildModal}
        closeModalUser={closeModalUser}
      />
    </>
  );
};

export default RoleModel;
